import type { Submission } from "@/api/submissions.service";
import type { SubmissionWithTranslations } from "@/hooks/useTranslations";
import { generateCsv, sanitizeUserInput } from "@/utils/csvExport";
import { formatDateShort } from "@/utils/dateFormatter";
import { getSubmissionPracticeFullName } from "@/utils/practiceName";

const CONTACT_HEADERS = ["Contact ID", "Contact name", "Contact match status"];

/**
 * `includeContactColumns` follows the `submission-entry-contact-match` flag: off, the
 * export is exactly what it was before DEV-2662.
 */
export function generateSubmissionsCsv(
  submissions: SubmissionWithTranslations[],
  { includeContactColumns = true }: { includeContactColumns?: boolean } = {},
): string {
  const baseHeaders = [
    "ID",
    "Firm",
    "Firm Ref",
    "Publication",
    "Edition",
    "Region",
    "Practice Group Name",
    "Practice Area Name",
    "Table Name",
    "Region (Translated)",
    "Practice Group (Translated)",
    "Practice Area (Translated)",
    "Table (Translated)",
    "Language Code",
    "Region ID",
    "Publication ID",
    "Editorial Category ID",
    "Practice Area ID",
    "Practice Group ID",
    "Table ID",
    "Created At",
    "Created By",
    "Close Date",
    "Submission Type",
    "Referee Type",
    "Person Name",
    "Document",
    "File URI",
    "Total Referees",
    "Total People",
    "Responses",
    "Referees",
  ];
  const personNameIndex = baseHeaders.indexOf("Person Name") + 1;
  const headers = includeContactColumns
    ? [
        ...baseHeaders.slice(0, personNameIndex),
        ...CONTACT_HEADERS,
        ...baseHeaders.slice(personNameIndex),
      ]
    : baseHeaders;

  return generateCsv(submissions, headers, submission => [
    String(submission.submissionId || ""),
    submission.firmName || "",
    String(submission.firmRef || ""),
    submission.publicationName || "",
    submission.edition || "",
    submission.regionName || "",
    submission.practiceGroupName || "",
    submission.practiceAreaName || "",
    submission.tableName || "",
    submission.regionTranslated || "",
    submission.practiceGroupTranslated || "",
    submission.practiceAreaTranslated || "",
    submission.tableTranslated || "",
    submission.languageCode || "",
    String(submission.regionId || ""),
    String(submission.publicationId || ""),
    String(submission.editorialCategoryId || ""),
    String(submission.practiceAreaId || ""),
    String(submission.practiceGroupId || ""),
    String(submission.tableId || ""),
    submission.createdAt ? formatDateShort(submission.createdAt) : "",
    submission.createdBy || "",
    submission.closeDate ? formatDateShort(submission.closeDate) : "",
    submission.submissionType || "",
    String(submission.refereeType || ""),
    sanitizeUserInput(submission.personName),
    ...(includeContactColumns
      ? [
          submission.contactId || "",
          // An Address Book snapshot, but still free text someone typed — same formula guard.
          sanitizeUserInput(submission.contactName),
          // Raw status (e.g. "auto_matched"), stable for spreadsheet filters and scripts.
          submission.contactMatchStatus || "",
        ]
      : []),
    submission.submissionDocx || "",
    submission.fileUri || "",
    String(submission.totalReferees || 0),
    String(submission.totalPeople || 0),
    String(submission.responseCount || 0),
    String(submission.referee),
  ]);
}
